import type { QueryClient } from '@tanstack/react-query';

import {
  TENANT_MODULES,
  isCurrent,
  visibleNavigationModules,
} from '@/app/layouts/tenantNavigation';
import { SESSION_QUERY_KEY } from '@/features/auth/useSession';
import { forgetAllRecentSearches } from '@/features/search/recent';
import type { PermissionSlug } from '@/shared/permissions/permissions';
import type { SessionPayload } from '@/shared/types/domain';

/**
 * OBS-007 / R19 — whose workspace this tab shows, and a safe landing when that
 * changes (the owner/CTO decision of 28 October 2026, R19 confirmed with
 * reauthorization, DIR-RC32-READINGS-20261028).
 *
 * An identity is one person in one organisation: the session's tenant and user,
 * by their opaque public ids.
 */

/** The workspace's safe default location: every seat has its dashboard. */
export const WORKSPACE_HOME = '/dashboard';

export type IdentityParts = readonly [tenantId: string, userId: string];

export const identityKey = (parts: IdentityParts): string => parts.join('|');

/*
 * What this tab last showed, remembered past a reload (sessionStorage: this tab
 * only, gone with it) and without naming anyone: a digest of the opaque ids, not
 * the ids. Read and written at once, so the answer is never one sign-in behind.
 * Where storage is refused, only this page's memory knows.
 */
const SHOWN_KEY = 'fidelen.workspace.v1.shown';

/** FNV-1a, 64 bits, as hex: a short, stable digest of the identity. */
function digest(parts: IdentityParts): string {
  let hash = 0xcbf29ce484222325n;

  for (const byte of new TextEncoder().encode(['workspace', ...parts].join('|'))) {
    hash = ((hash ^ BigInt(byte)) * 0x100000001b3n) & 0xffffffffffffffffn;
  }

  return hash.toString(16).padStart(16, '0');
}

function tabStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export type Relation = 'none' | 'same' | 'other';

/** Whether this tab last showed this identity, another one, or none it remembers. */
export function relationToShown(parts: IdentityParts): Relation {
  let shown: string | null = null;

  try {
    shown = tabStorage()?.getItem(SHOWN_KEY) ?? null;
  } catch {
    shown = null;
  }

  if (shown === null) return 'none';

  return shown === digest(parts) ? 'same' : 'other';
}

export function rememberShown(parts: IdentityParts): void {
  try {
    tabStorage()?.setItem(SHOWN_KEY, digest(parts));
  } catch {
    // Refused (a private window, the quota): this page's memory still knows.
  }
}

/**
 * Nothing read before stays: every cached read and every mutation's state,
 * except the session that has just been signed in. Nothing renders from the
 * cache meanwhile — the gate shows no workspace until this is done.
 */
export function startAfresh(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) =>
      !(query.queryKey.length === 1 && query.queryKey[0] === SESSION_QUERY_KEY[0]),
  });
  queryClient.getMutationCache().clear();
}

/**
 * What the previous person kept in this tab beyond the cache: their recent
 * searches (sessionStorage), forgotten as a sign-out already forgets them.
 *
 * Guide progress stays: each person's, in each organisation, is kept under its
 * own hashed key, which no other identity reads, and is there again when they
 * come back (`e2e/guidance-organisations.spec.ts`). The display language is
 * this device's, chosen on the sign-in screen itself, and stays too.
 */
export function forgetOtherPeople(): void {
  forgetAllRecentSearches();
}

/**
 * The route's own gate, as the navigation applies it to this session: a
 * destination the session may not reach is not opened again for it. A path no
 * navigation entry declares (My profile, Sign-in security) has no gate here;
 * its page, and the server, decide.
 */
export function mayOpen(pathname: string, session: SessionPayload): boolean {
  const declared = TENANT_MODULES.flatMap((module) => module.items).filter((item) =>
    isCurrent(item, pathname),
  );

  if (declared.length === 0) return true;

  const held = new Set(session.permissions);
  const visible = visibleNavigationModules({
    can: (permission: PermissionSlug) => held.has(permission),
    canAny: (permissions: PermissionSlug[]) =>
      permissions.some((permission) => held.has(permission)),
    canReach: (surface) => session.effective_authority?.[surface] === true,
  }).flatMap((module) => module.items);

  return declared.some((item) => visible.includes(item));
}
